<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ColorInput from 'dashboard/components-next/colorpicker/ColorInput.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import {
  applyPreset,
  GOOGLE_FONT_OPTIONS,
  resolveFontFamily,
  STYLE_PRESET_OPTIONS,
} from '../lib/portalTheme';

const props = defineProps({
  name: { type: String, default: '' },
  theme: { type: Object, default: () => ({}) },
  isDefault: { type: Boolean, default: false },
  portalEditableAttributeKeys: { type: Array, default: () => [] },
  eventAttributeOptions: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'update:name',
  'update:theme',
  'update:isDefault',
  'update:portalEditableAttributeKeys',
]);

const { t } = useI18n();

const updateThemeField = (key, value) => {
  emit('update:theme', { ...props.theme, [key]: value });
};

const onPresetChange = value => {
  emit('update:theme', applyPreset(props.theme, value));
};

const fontModel = computed({
  get: () => resolveFontFamily(props.theme.font),
  set: value => updateThemeField('font', value || 'Inter'),
});

const presetOptions = computed(() =>
  STYLE_PRESET_OPTIONS.map(opt => ({
    value: opt.value,
    label: t(
      `PLANNING.EVENT_PORTALS.EDITOR.THEME.PRESETS.${opt.value.toUpperCase()}`
    ),
  }))
);
</script>

<template>
  <div class="space-y-5">
    <div>
      <label class="block text-sm font-medium text-n-slate-11 mb-1">
        {{ t('PLANNING.EVENT_PORTALS.FORM.NAME') }}
      </label>
      <input
        :value="name"
        type="text"
        class="reset-base w-full px-3 py-2 text-sm border border-n-slate-6 rounded-lg bg-n-slate-1 text-n-slate-12 focus:border-n-brand-7 outline-none"
        @input="emit('update:name', $event.target.value)"
      />
    </div>

    <div>
      <label class="block text-sm font-medium text-n-slate-11 mb-1">
        {{ t('PLANNING.EVENT_PORTALS.FORM.EDITABLE_ATTRIBUTES') }}
      </label>
      <p class="text-xs text-n-slate-9 mb-2">
        {{ t('PLANNING.EVENT_PORTALS.FORM.EDITABLE_ATTRIBUTES_HINT') }}
      </p>
      <TagMultiSelectComboBox
        :model-value="portalEditableAttributeKeys"
        :options="eventAttributeOptions"
        :placeholder="
          t('PLANNING.EVENT_PORTALS.FORM.EDITABLE_ATTRIBUTES_PLACEHOLDER')
        "
        @update:model-value="
          emit('update:portalEditableAttributeKeys', $event || [])
        "
      />
    </div>

    <div>
      <label class="block text-sm font-medium text-n-slate-11 mb-1">
        {{ t('PLANNING.EVENT_PORTALS.FORM.STYLE') }}
      </label>
      <select
        :value="theme.style || 'modern'"
        class="w-full px-3 py-2 text-sm border border-n-slate-6 rounded-lg bg-n-slate-1 text-n-slate-12"
        @change="onPresetChange($event.target.value)"
      >
        <option
          v-for="opt in presetOptions"
          :key="opt.value"
          :value="opt.value"
        >
          {{ opt.label }}
        </option>
      </select>
    </div>

    <div>
      <label class="block text-sm font-medium text-n-slate-11 mb-1">
        {{ t('PLANNING.EVENT_PORTALS.FORM.ACCENT_COLOR') }}
      </label>
      <ColorInput
        :model-value="theme.accent_color || '#7c3aed'"
        @update:model-value="
          updateThemeField('accent_color', $event || '#7c3aed')
        "
      />
    </div>

    <div>
      <label class="block text-sm font-medium text-n-slate-11 mb-1">
        {{ t('PLANNING.EVENT_PORTALS.FORM.FONT') }}
      </label>
      <ComboBox
        v-model="fontModel"
        :options="GOOGLE_FONT_OPTIONS"
        :placeholder="t('PLANNING.EVENT_PORTALS.EDITOR.THEME.FONT_PLACEHOLDER')"
      />
    </div>

    <label class="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        class="rounded border-n-slate-6 text-n-brand-9 focus:ring-n-brand-7"
        :checked="isDefault"
        @change="emit('update:isDefault', $event.target.checked)"
      />
      <span class="text-sm text-n-slate-11">
        {{ t('PLANNING.EVENT_PORTALS.FORM.IS_DEFAULT') }}
      </span>
    </label>
  </div>
</template>
